import { isString } from '@mcwd/typescript-type-guards';
import type { SiteCountryCodeUppercase } from '../../../../types-and-definitions/locale-defs/Locales.js';
import type { GatingLevel } from '../gating/gating-level.js';
import type { GatingState } from '../gating/gating-state.js';

export const UserMarketoInputName = [
  "FirstName",
  "LastName",
  "Email",
  "Company",
  "Country",
  "State",
  "Phone",
  "Tier__c",
  "Industry",
  "Department__c",
  "Email_Opt_In__c",
  "contactRequested",
  "Webform_Comments_recent_value__c",
  "Latest_Solution_Interest__c",
  "Partner_Opt_in_Processing__c",
] as const satisfies readonly string[];
type AnyUserMarketoInputName = typeof UserMarketoInputName[number];
export type UserMarketoInputName<InputNames extends AnyUserMarketoInputName = AnyUserMarketoInputName> = InputNames;


export const SimpleForm_HiddenMarketoInputName = [
  "SiteLanguageCode",
  "FormName",
  "source",
  "Source_String__c",
  "Source_Website",
  "Medium__c",
  "Campaign__c",
  "Source__c",
  "Last_Offer_Campaign__c",
  "Website_Language_Code__c",
  "GCLID__c",
  "Mobile__c",
] as const;
export type SimpleForm_HiddenMarketoInputName = typeof SimpleForm_HiddenMarketoInputName[ number ];

export const HiddenMarketoInputName = [
  ...SimpleForm_HiddenMarketoInputName,
  "Last_Offer_Name__c",
  "documentDownloadURL",
  "roi_querystring__c_contact",
  "initialFormGatingLevel",
  "initialFormExperienceGatingLevel",
  "formGatingState",
  "experienceGatingState",
  "multiDocSelectorFormFill"
] as const;
export type HiddenMarketoInputName = typeof HiddenMarketoInputName[ number ];


export const AnyMarketoInputName = [...UserMarketoInputName, ...HiddenMarketoInputName] as const;
export type AnyMarketoInputName = typeof AnyMarketoInputName[number];


export function isHiddenMarketoInputName(value: unknown): value is HiddenMarketoInputName {
  return isString(value) && (HiddenMarketoInputName as readonly string[]).includes(value);
}

export function isUserMarketoInputName(value: unknown): value is UserMarketoInputName {
  return isString(value) && UserMarketoInputName.includes(value as UserMarketoInputName);
}

export function isAnyMarketoInputName(value: unknown): value is AnyMarketoInputName {
  return isString(value) && AnyMarketoInputName.includes(value as AnyMarketoInputName);
}


type MapInputNamesToAllowedTypes<MarketoName extends AnyMarketoInputName, AllowedValueTypes extends string | string[] | number | boolean> = {
  [Key in MarketoName]: AllowedValueTypes
};

type UserInputsTypeMap<T extends MapInputNamesToAllowedTypes<UserMarketoInputName, string | string[] | number | boolean>> = T;
type SimpleForm_HiddenInputsTypeMap<T extends MapInputNamesToAllowedTypes<SimpleForm_HiddenMarketoInputName, string | boolean>> = T;
type HiddenInputsTypeMap<T extends MapInputNamesToAllowedTypes<HiddenMarketoInputName, string | boolean>> = T;


export type UserMarketoInputType = UserInputsTypeMap<{
  FirstName: string;
  LastName: string;
  Email: string;
  Company: string;
  Country: string;
  State: string;
  Phone: string;
  Tier__c: string;
  Industry: string;
  Department__c: string;
  Email_Opt_In__c: boolean;
  contactRequested: string;
  Webform_Comments_recent_value__c: string;
  Latest_Solution_Interest__c: string;
  Partner_Opt_in_Processing__c: boolean;
}>;


export type SimpleForm_HiddenMarketoInputType = SimpleForm_HiddenInputsTypeMap<{
  SiteLanguageCode: SiteCountryCodeUppercase;
  Website_Language_Code__c: SiteCountryCodeUppercase;
  FormName: string;
  source: string;
  Source_String__c: string;
  Source_Website: string;
  Medium__c: string;
  Campaign__c: string;
  Source__c: string;
  Last_Offer_Campaign__c: string;
  GCLID__c: string;
  Mobile__c: boolean;
}>;


export type HiddenMarketoInputType = HiddenInputsTypeMap<{
  SiteLanguageCode: SiteCountryCodeUppercase;
  Website_Language_Code__c: SiteCountryCodeUppercase;
  FormName: string;
  source: string;
  Source_String__c: string;
  Source_Website: string;
  Medium__c: string;
  Campaign__c: string;
  Source__c: string;
  Last_Offer_Campaign__c: string;
  Last_Offer_Name__c: string;
  documentDownloadURL: string;
  GCLID__c: string;
  Mobile__c: boolean;
  roi_querystring__c_contact: string;
  experienceGatingState: GatingState.FormExperience;
  formGatingState: GatingState.MarketoForm;
  initialFormGatingLevel: GatingLevel.MarketoForm;
  initialFormExperienceGatingLevel: GatingLevel.FormExperience;
  multiDocSelectorFormFill: boolean;
}>;


export type AnyMarketoInputType = UserMarketoInputType & HiddenMarketoInputType;

export type GetMarketoInputTypeFromName<TName> = 
  TName extends UserMarketoInputName
    ? UserMarketoInputType[TName]
    :
  TName extends HiddenMarketoInputName
    ? HiddenMarketoInputType[TName]
    : never;

export type MarketoVals = {
  [Key in AnyMarketoInputName]?: GetMarketoInputTypeFromName<Key> | null;
};